import {
  Box,
  CommandFooter,
  Kbd,
  Text,
  useKeyboardHints,
  type Omittable,
} from "@salestrak/ui";

/**
 * Shared footer for the desktop global-search command dialog.
 * Renders the library CommandFooter so it slots under a CommandPanel — the
 * panel keys its bottom seam off the `data-slot="command-footer"` sibling.
 * The left side is a fixed keyboard legend; the right side is contextual to
 * the currently-highlighted row — `useGlobalSearchContent` resolves it and
 * hands it down as `context`. Omitted on mobile (touch has no arrow keys).
 */
export function GlobalSearchFooter({
  context,
  enterVerb = "open",
}: Omittable<{
  context?: string;
  /** The legend's Enter verb — "open" for every row but an address the
   *  viewer can create a project at, which reads "create". */
  enterVerb?: "open" | "create";
}>) {
  // The legend is prose ABOUT keys ("Press / …, hit Enter …"), so the caps
  // standing down would leave a sentence with holes; the whole legend goes.
  const keyboardHints = useKeyboardHints();
  return (
    // `relative` lifts the footer ABOVE the popup's two-tone veil. The veil is
    // an absolutely-positioned `::before` on the popup, so it paints over the
    // statically-positioned footer and washes its text/Kbd — the same reason
    // the (relative) CommandPanel escapes it. Positioned, the footer paints on
    // top of the veil in tree order: its transparent fill still shows the veil
    // as the background, but the ink lands crisp on top instead of under it.
    // px-4 keeps the legend on the panel's 16px content grid (list frame + item
    // inset, scope chips) — the stock px-5 sits 4px off it. The
    // `text-popover-foreground/80` override then reads at true strength on the
    // veiled background (a step below the panel's body text); the legend Texts
    // drop their own `muted` variant to inherit it.
    <CommandFooter className="px-4 relative text-popover-foreground/60">
      {keyboardHints ? (
        <Box direction="row" align="center" gap="xs">
          <Text size="xs">Press</Text>
          <Kbd className="text-popover-foreground/80">/</Kbd>
          <Text size="xs">to see commands, hit</Text>
          <Kbd className="text-popover-foreground/80">Enter</Kbd>
          <Text size="xs">to {enterVerb}</Text>
        </Box>
      ) : (
        <Box />
      )}
      {context ? <Text size="xs">{context}</Text> : null}
    </CommandFooter>
  );
}
